<mat-card>
  <mat-card-content class="main-card-content">
    <div class="card-header">Auto withdrawals configuration</div>
    @if (formGroup) {
      <form [formGroup]="formGroup">
        <div class="config-param">
          <mat-checkbox
            [disabled]="!editMode"
            class="inline"
            matTooltip="Active"
            formControlName="requestToPendingEnabled"></mat-checkbox>
          <div class="inline spacing">
            <div>
              <span class="label inline">Enable auto pending after: </span>
              <input
                class="inline-input"
                [readOnly]="!editMode"
                type="number"
                formControlName="requestedToPendingInMinutes" />
              <span class="label inline">minutes</span>
            </div>
            <span class="description"
              >If active, then system will automatically move request to PENDING status, if request was submitted at
              least X minutes ago</span
            >
          </div>
        </div>
        <div class="config-param">
          <mat-checkbox
            [disabled]="!editMode"
            class="inline"
            matTooltip="Active"
            formControlName="pendingToAcceptedEnabled"></mat-checkbox>
          <div class="inline spacing">
            <div>
              <span class="label inline">Enable auto accepting after: </span>
              <input
                class="inline-input"
                [readonly]="!editMode"
                type="number"
                formControlName="pendingToAcceptedInMinutes" />
              <span class="label inline">minutes</span>
            </div>
            <span class="description"
              >If active, then system will automatically start acceptation process, if request was submitted at least X
              minutes ago</span
            >
          </div>
        </div>
        <div class="config-param">
          <div class="inline">
            <mat-checkbox
              [disabled]="!editMode"
              class="inline"
              matTooltip="Active"
              formControlName="enableWithdrawCancellation"></mat-checkbox>
            <div class="inline spacing">
              <div>
                <span class="label inline">Cancelling withdrawals is allowed up to </span>
                <input
                  class="inline-input"
                  [readonly]="!editMode"
                  type="number"
                  formControlName="maxNumberOfCancelWithdrawalsPerMonth" />
                <span class="label inline">times per month</span>
              </div>
              <span class="description"
                >If inactive, or active and set to 0, then cancelling withdrawals will be impossible</span
              >
            </div>
          </div>
        </div>
        <div class="config-param">
          <div class="inline spacing-big">
            <span class="label">Withdraws article</span><br />
            <span class="description">It is presented as payments help on withdrawal modal</span>
          </div>
          <div class="inline spacing-and-size">
            <app-lookup-from-field
              [lookup]="'zendesk_article'"
              [fieldConfig]="articlesLookupConfig"
              [data]="formGroup.get('withdrawsArticle')?.value"
              [editMode]="editMode"
              [formGroup]="formGroup">
            </app-lookup-from-field>
          </div>
        </div>
        @if (editMode) {
          <div>
            <a (click)="editMode = false">
              <button mat-stroked-button>Cancel</button>
            </a>
            <button
              class="update-button"
              mat-raised-button
              color="primary"
              [disabled]="!formGroup?.valid"
              (click)="update()">
              Update
            </button>
          </div>
        }
        @if (!editMode) {
          <div>
            <a (click)="editMode = true">
              <button mat-stroked-button color="primary">Edit</button>
            </a>
          </div>
        }
      </form>
    }

    <div class="global-settings">Global settings</div>
    <div class="content-adjust">
      <div class="hint">
        <i class="fas fa-question-circle"></i> These settings will be applied to all tiers, alongside their existing
        configurations.
      </div>
    </div>
    <div class="range">BIN range blocking</div>
    <div class="content-adjust">
      <div class="hint">
        <i class="fas fa-question-circle"></i> If the card matches one of these BIN ranges, it will not be permitted to
        go through automatically, even if the tiers below allow it.
      </div>
    </div>
    @if (config?.tiers) {
      <div class="example-list" cdkDropList (cdkDropListDropped)="drop($event)">
        @for (binRangeExclusion of config?.binRangeExclusions; track binRangeExclusion) {
          <div class="example-box" cdkDrag>
            <div>
              <span class="inline bold-info">#{{ binRangeExclusion.id }}</span>
              <div class="info-icon">
                <i class="fas fa-edit" (click)="editBinRangeExclusion(binRangeExclusion.id)"></i>
              </div>
              <div class="info-icon">
                <i class="fas fa-trash-alt" (click)="removeBinRangeExclusion(binRangeExclusion.id)"></i>
              </div>
            </div>
            @if (binRangeExclusion.paymentSolutions) {
              <div class="property">
                <label class="property-header">Payment Solutions</label>
                <div class="property-content">
                  {{ binRangeExclusion.paymentSolutions }}
                </div>
              </div>
            }
            @if (binRangeExclusion.binRange) {
              <div class="property">
                <label class="property-header">Bin range</label>
                <div class="property-content">
                  {{ binRangeExclusion.binRange }}
                </div>
              </div>
            }
          </div>
        }
      </div>
    }
    <a (click)="addNewBinRangeExclusion()">
      <button mat-stroked-button color="primary">Add new Bin Range Exclusion</button>
    </a>

    <div class="tiers-container">Tiers configuration</div>
    <div class="content-adjust">
      <div class="hint">
        <i class="fas fa-question-circle"></i> Request will be accepted automatically if it meets all criteria from at
        least one of the below active tiers.
      </div>
      <div class="hint">
        <i class="fas fa-question-circle"></i> Tiers are processed in order starting from the top one. Order can be
        changed by moving tier to different position.
      </div>
    </div>

    @if (config?.tiers) {
      <div class="example-list" cdkDropList (cdkDropListDropped)="drop($event)">
        @for (tier of config?.tiers; track tier) {
          <div class="example-box" cdkDrag>
            <div>
              <mat-checkbox class="inline" disabled [checked]="tier.active"></mat-checkbox>
              <span class="inline bold-info">#{{ tier.id }}</span>
              <div class="edit-tier">
                <i class="fas fa-edit" (click)="editTier(tier.id)"></i>
              </div>
              <div class="remove-tier">
                <i class="fas fa-trash-alt" (click)="removeTier(tier.id)"></i>
              </div>
            </div>
            @if (tier.amountActive) {
              <div class="property">
                <label class="property-header">Request amount</label>
                <div class="property-content">
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.minAmount"></app-form-field-money>
                  <span class="inline">to</span>
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.maxAmount"></app-form-field-money>
                </div>
              </div>
            }
            @if (tier.amountPerDayActive) {
              <div class="property">
                <label class="property-header">Value of requests today</label>
                <div class="property-content">
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.minAmountPerDay"></app-form-field-money>
                  <span class="inline">to</span>
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.maxAmountPerDay"></app-form-field-money>
                </div>
              </div>
            }
            @if (tier.requestsPerDayActive) {
              <div class="property">
                <label class="property-header">Number of requests today</label>
                <div class="property-content">{{ tier.minRequestsPerDay }} to {{ tier.maxRequestsPerDay }}</div>
              </div>
            }
            @if (tier.lifetimeAmountActive) {
              <div class="property">
                <label class="property-header">Value of requests in lifetime</label>
                <div class="property-content">
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.minLifetimeAmount"></app-form-field-money>
                  <span class="inline">to</span>
                  <app-form-field-money
                    [fieldConfig]="moneyConfig"
                    class="inline"
                    [data]="tier.maxLifetimeAmount"></app-form-field-money>
                </div>
              </div>
            }
            @if (tier.lifetimeRequestsActive) {
              <div class="property">
                <label class="property-header">Number of requests in lifetime</label>
                <div class="property-content">{{ tier.minLifetimeRequests }} to {{ tier.maxLifetimeRequests }}</div>
              </div>
            }
            @if (tier.cardsNumberActive) {
              <div class="property">
                <label class="property-header">Active cards</label>
                <div class="property-content">
                  <span> {{ tier.cardsNumberMin }} to {{ tier.cardsNumberMax }} </span>
                </div>
              </div>
            }
            @if (tier.cardAgeActive) {
              <div class="property">
                <label class="property-header">Days since card was added is more than</label
                ><label>(Only for CARD requests)</label>
                <div class="property-content">
                  <input matInput class="inline" disabled type="number" [value]="tier.minCardAgeDays.toString()" />
                </div>
              </div>
            }
            @if (tier.cardDocumentVerified) {
              <div class="property">
                <label class="property-header">Card has been successfully verified with documents</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.cardDocumentVerified"></mat-checkbox>
                </div>
              </div>
            }
            @if (tier.avsResultActive) {
              <div class="property">
                <span class="property-header">Last AVS result was one of</span>
                <div class="property-content">
                  @for (result of tier.avsResultValues; track result) {
                    @if (result === 'A') {
                      <div class="inline">
                        <mat-checkbox class="inline" disabled [checked]="true"></mat-checkbox>
                        <span class="inline">{{ avsResultValues[0].name }}</span>
                      </div>
                    }
                    @if (result === '7') {
                      <div class="inline">
                        <mat-checkbox class="inline" disabled [checked]="true"></mat-checkbox>
                        <span class="inline">{{ avsResultValues[1].name }}</span>
                      </div>
                    }
                    @if (result === 'C') {
                      <div class="inline">
                        <mat-checkbox class="inline" disabled [checked]="true"></mat-checkbox>
                        <span class="inline">{{ avsResultValues[2].name }}</span>
                      </div>
                    }
                    @if (result === '6') {
                      <div class="inline">
                        <mat-checkbox class="inline" disabled [checked]="true"></mat-checkbox>
                        <span class="inline">{{ avsResultValues[3].name }}</span>
                      </div>
                    }
                    @if (result === 'F') {
                      <div class="inline">
                        <mat-checkbox class="inline" disabled [checked]="true"></mat-checkbox>
                        <span class="inline">{{ avsResultValues[4].name }}</span>
                      </div>
                    }
                  }
                </div>
              </div>
            }
            @if (tier.riskScoreActive) {
              <div class="property">
                <label class="property-header">Highest risk score for payment type is less than</label>
                <div class="property-content">
                  <input matInput class="inline" disabled type="number" [value]="tier.maxRiskScore.toString()" />
                </div>
              </div>
            }
            @if (tier.totalCardsNumberActive) {
              <div class="property">
                <label class="property-header">Total cards</label>
                <div class="property-content">
                  <span> {{ tier.totalCardsNumberMin }} to {{ tier.totalCardsNumberMax }} </span>
                </div>
              </div>
            }
            @if (tier.acceptedForCardActive) {
              <div class="property">
                <label class="property-header">Accepted requests for card</label>
                <div class="property-content">{{ tier.minAcceptedForCard }} to {{ tier.maxAcceptedForCard }}</div>
              </div>
            }
            @if (tier.bonusAbuseA || tier.bonusAbuseB || tier.bonusAbuseC) {
              <div class="property">
                <span class="property-header">Include bonus abusers of type</span>
                <div class="property-content">
                  @if (tier.bonusAbuseA) {
                    <div class="inline">
                      <span class="inline">A</span>
                      <mat-checkbox class="inline" disabled [checked]="tier.bonusAbuseA"></mat-checkbox>
                    </div>
                  }
                  @if (tier.bonusAbuseB) {
                    <div class="inline">
                      <span class="inline">B</span>
                      <mat-checkbox class="inline" disabled [checked]="tier.bonusAbuseB"></mat-checkbox>
                    </div>
                  }
                  @if (tier.bonusAbuseC) {
                    <div class="inline">
                      <span class="inline">C</span>
                      <mat-checkbox class="inline" disabled [checked]="tier.bonusAbuseC"></mat-checkbox>
                    </div>
                  }
                </div>
              </div>
            }
            @if (tier.withdrawWatch) {
              <div class="property">
                <label class="property-header">Allow players with withdraw watch</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.withdrawWatch"> </mat-checkbox>
                </div>
              </div>
            }
            @if (tier.hvp) {
              <div class="property">
                <label class="property-header">Only for HVP</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.hvp"> </mat-checkbox>
                </div>
              </div>
            }
            @if (tier.allowOnDataMismatch) {
              <div class="property">
                <label class="property-header">Allow on data mismatch</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.allowOnDataMismatch"> </mat-checkbox>
                </div>
              </div>
            }
            @if (tier.withdrawLteDepositsForPaymentMethod) {
              <div class="property">
                <label class="property-header">Withdrawals <= Deposits for selected method.</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.withdrawLteDepositsForPaymentMethod">
                  </mat-checkbox>
                </div>
              </div>
            }
            @if (tier.minCashStakesToDepositsRatioActive) {
              <div class="property">
                <label class="property-header">Min cash stakes to deposits ratio</label>
                <div class="property-content">
                  <input
                    matInput
                    class="inline"
                    disabled
                    type="number"
                    [value]="tier.minCashStakesToDepositsRatio.toString()" />
                </div>
              </div>
            }
            @if (tier.trustedCard) {
              <div class="property">
                <label class="property-header">Trusted cards</label>
                <div class="property-content">
                  <mat-checkbox class="inline" disabled [checked]="tier.trustedCard"></mat-checkbox>
                </div>
              </div>
            }
          </div>
        }
      </div>
    }

    <button (click)="addNewTier()" mat-stroked-button color="primary">Add new tier</button>
  </mat-card-content>
</mat-card>
